import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useDateRange } from '@/contexts/DateRangeContext';
import { useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import {
  Search, Target, DollarSign, TrendingUp, Activity,
  Eye, MousePointer, Users, Clock, ChevronLeft, ChevronRight, Play, Image as ImageIcon
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'active', label: 'Activas' },
  { key: 'paused', label: 'Pausadas' },
  { key: 'archived', label: 'Archivadas' },
];

const SORT_OPTIONS = [
  { key: 'spent_desc', label: 'Mayor gasto' },
  { key: 'spent_asc', label: 'Menor gasto' },
  { key: 'ctr_desc', label: 'Mejor CTR' },
  { key: 'roi_desc', label: 'Mejor ROI' },
  { key: 'conv_desc', label: 'Más conversiones' },
  { key: 'name_asc', label: 'Nombre A-Z' },
];

export default function Campaigns() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const { range } = useDateRange();
  const theme = useTheme();
  const clientIdRef = useRef(clientId);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('spent_desc');

  useEffect(() => { clientIdRef.current = clientId; }, [clientId]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);
      try {
        const cid = clientIdRef.current;
        const df = range?.from;
        const dt = range?.to;
        const qs = new URLSearchParams();
        if (cid) qs.set('client_id', cid);
        if (df) qs.set('date_from', df);
        if (dt) qs.set('date_to', dt);
        const url = `${API}/campaigns?${qs.toString()}`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        setCampaigns(res.data || []);
      } catch (e) {
        console.error('Failed to fetch campaigns:', e);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, [clientId, range?.from, range?.to, token]);

  // Contadores por status
  const filtered = useMemo(() => {
    let list = [...campaigns];
    if (statusFilter !== 'all') {
      list = list.filter(c => {
        if (statusFilter === 'paused') return c.status !== 'active' && c.status !== 'archived';
        return c.status === statusFilter;
      });
    }
    if (platformFilter !== 'all') list = list.filter(c => c.platform === platformFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => (c.name || '').toLowerCase().includes(q));
    }
    switch (sortBy) {
      case 'spent_desc': list.sort((a, b) => (b.spent || 0) - (a.spent || 0)); break;
      case 'spent_asc': list.sort((a, b) => (a.spent || 0) - (b.spent || 0)); break;
      case 'ctr_desc': list.sort((a, b) => (b.ctr || 0) - (a.ctr || 0)); break;
      case 'roi_desc': list.sort((a, b) => (b.roi || 0) - (a.roi || 0)); break;
      case 'conv_desc': list.sort((a, b) => (b.conversions || 0) - (a.conversions || 0)); break;
      case 'name_asc': list.sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
      default: break;
    }
    return list;
  }, [campaigns, statusFilter, platformFilter, search, sortBy]);

  const counts = useMemo(() => {
    const c = { all: filtered.length, active: 0, paused: 0, archived: 0 };
    filtered.forEach(camp => {
      if (camp.status === 'active') c.active++;
      else if (camp.status === 'archived') c.archived++;
      else c.paused++;
    });
    return c;
  }, [filtered]);

  // Stats totales
  const stats = useMemo(() => {
    const totalSpent = filtered.reduce((s, c) => s + (c.spent || 0), 0);
    const totalConv = filtered.reduce((s, c) => s + (c.conversions || 0), 0);
    const totalClicks = filtered.reduce((s, c) => s + (c.clicks || 0), 0);
    const totalImp = filtered.reduce((s, c) => s + (c.impressions || 0), 0);
    const avgCtr = totalImp > 0 ? (totalClicks / totalImp) * 100 : 0;
    return { totalSpent, totalConv, avgCtr, totalClicks };
  }, [filtered]);

  // Filtrar + ordenar


  const statusBadge = (status) => {
    if (status === 'active') return 'bg-success/20 text-success border-success/30';
    if (status === 'archived') return 'bg-secondary/20 text-secondary border-secondary/30';
    return 'bg-warning/20 text-warning border-warning/30';
  };

  const statusLabel = (status) => {
    if (status === 'active') return 'Activa';
    if (status === 'archived') return 'Archivada';
    return 'Pausada';
  };

  const platformLabel = (p) => {
    if (p === 'meta') return 'Meta';
    if (p === 'google') return 'Google';
    if (p === 'tiktok') return 'TikTok';
    return p || '—';
  };

  const platformColor = (p) => {
    if (p === 'meta') return 'bg-white/90 text-brand-meta';
    if (p === 'google') return 'bg-white/90 text-brand-google';
    return 'bg-white/90 text-gray-700';
  };

  // Iniciales del nombre
  const getInitials = (name) => {
    if (!name) return '?';
    const clean = name.replace(/[^a-zA-Z0-9 ]/g, '').trim();
    const words = clean.split(/\s+/).filter(w => w.length > 0);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Gradient corporate determinista
  const getGradient = (name) => {
    const gradients = [
      'from-slate-700 to-slate-900',
      'from-blue-800 to-slate-900',
      'from-slate-600 to-slate-800',
      'from-blue-900 to-slate-800',
      'from-slate-700 to-blue-900',
      'from-gray-700 to-slate-900',
      'from-blue-700 to-blue-900',
    ];
    const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return gradients[hash % gradients.length];
  };

  const formatNum = (n) => (n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const formatCurr = (n) => `$${formatNum(n)}`;

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-screen">
        <div className={`text-xl ${theme.text}`}>Cargando campañas...</div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="campaigns-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className={`font-heading font-bold text-4xl mb-1 ${theme.text}`}>Campañas</h1>
          <p className={`text-sm ${theme.textSecondary}`}>
            Administra tus campañas publicitarias · {range?.label || 'Últimos 30 días'}
          </p>
        </motion.div>

        {/* Stats cards */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          <div className={`${theme.glassCard} rounded-2xl p-4`}>
            <div className={`flex items-center gap-2 ${theme.textSecondary} text-xs mb-2`}>
              <Target className="w-4 h-4" /><span>Total campañas</span>
            </div>
            <p className={`text-2xl font-bold ${theme.text}`}>{counts.all}</p>
            <p className={`text-xs ${theme.textSecondary} mt-1`}>{counts.active} activas · {counts.paused} pausadas</p>
          </div>
          <div className={`${theme.glassCard} rounded-2xl p-4`}>
            <div className={`flex items-center gap-2 ${theme.textSecondary} text-xs mb-2`}>
              <DollarSign className="w-4 h-4" /><span>Gasto total</span>
            </div>
            <p className={`text-2xl font-bold ${theme.text}`}>{formatCurr(stats.totalSpent)}</p>
          </div>
          <div className={`${theme.glassCard} rounded-2xl p-4`}>
            <div className={`flex items-center gap-2 ${theme.textSecondary} text-xs mb-2`}>
              <MousePointer className="w-4 h-4" /><span>Clicks</span>
            </div>
            <p className={`text-2xl font-bold ${theme.text}`}>{formatNum(stats.totalClicks)}</p>
            <p className={`text-xs ${theme.textSecondary} mt-1`}>CTR {stats.avgCtr.toFixed(2)}%</p>
          </div>
          <div className={`${theme.glassCard} rounded-2xl p-4`}>
            <div className={`flex items-center gap-2 ${theme.textSecondary} text-xs mb-2`}>
              <Activity className="w-4 h-4 text-success" /><span>Conversiones</span>
            </div>
            <p className={`text-2xl font-bold ${theme.text}`}>{formatNum(stats.totalConv)}</p>
          </div>
        </motion.div>

        {/* Filtros */}
        {campaigns.length > 0 && (
          <div className={`${theme.glassCard} rounded-2xl p-4 mb-6`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 flex-wrap">
                {STATUS_FILTERS.map(f => (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      statusFilter === f.key
                        ? 'bg-gradient-to-r from-ai-glow to-brand-meta text-white'
                        : `${theme.textSecondary} ${theme.cardHover} border ${theme.border}`
                    }`}
                  >
                    {f.label} <span className="opacity-70">({counts[f.key]})</span>
                  </button>
                ))}
              </div>
              <div className="flex-1" />
              <div className="relative">
                <Search className={`w-4 h-4 ${theme.textSecondary} absolute left-3 top-1/2 -translate-y-1/2`} />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className={`pl-9 w-48 h-9 ${theme.input}`} />
              </div>
              <Select value={platformFilter} onValueChange={setPlatformFilter}>
                <SelectTrigger className={`h-9 w-36 ${theme.input}`}><SelectValue /></SelectTrigger>
                <SelectContent className={theme.dropdown}>
                  <SelectItem value="all">Todas plataformas</SelectItem>
                  <SelectItem value="meta">Meta</SelectItem>
                  <SelectItem value="google">Google</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className={`h-9 w-40 ${theme.input}`}><SelectValue /></SelectTrigger>
                <SelectContent className={theme.dropdown}>
                  {SORT_OPTIONS.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {filtered.length !== campaigns.length && (
              <p className={`text-xs ${theme.textSecondary} mt-3`}>
                Mostrando {filtered.length} de {campaigns.length}
              </p>
            )}
          </div>
        )}

        {/* Grid */}
        {campaigns.length === 0 ? (
          <div className={`text-center py-16 ${theme.glassCard} rounded-2xl`}>
            <Target className={`w-16 h-16 mx-auto mb-4 ${theme.textSecondary}`} />
            <p className={`${theme.textSecondary} text-lg`}>No hay campañas con actividad en este rango</p>
            <p className={`text-xs ${theme.textSecondary} mt-2 opacity-70`}>Probá cambiar el período arriba</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className={`text-center py-16 ${theme.glassCard} rounded-2xl`}>
            <Search className={`w-12 h-12 mx-auto mb-3 ${theme.textSecondary}`} />
            <p className={theme.textSecondary}>Sin resultados para estos filtros</p>
            <button onClick={() => { setStatusFilter('all'); setPlatformFilter('all'); setSearch(''); }} className="mt-3 text-sm text-ai-glow hover:underline">
              Limpiar filtros
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c, index) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.02, 0.3) }}
                className={`${theme.glassCard} rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform`}
              >
                {/* Header limpio con borde de color */}
                <div className={`h-1 w-full ${c.platform === 'meta' ? 'bg-blue-500' : c.platform === 'google' ? 'bg-red-500' : c.platform === 'tiktok' ? 'bg-gray-900' : 'bg-indigo-400'}`} />
                <div className="relative px-4 pt-3 pb-2 flex items-center justify-between">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${platformColor(c.platform)}`}>
                    {platformLabel(c.platform)}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusBadge(c.status)}`}>
                    {statusLabel(c.status)}
                  </span>
                </div>

                {/* Body */}
                <div className="p-4">
                  <h3 className={`font-heading font-semibold text-base mb-3 line-clamp-2 ${theme.text}`} title={c.name}>
                    {c.name}
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <Metric theme={theme} label="Gasto" value={formatCurr(c.spent)} />
                    <Metric theme={theme} label="Conversiones" value={formatNum(c.conversions)} />
                    <Metric theme={theme} label="Impresiones" value={formatNum(c.impressions)} />
                    <Metric theme={theme} label="Clicks" value={formatNum(c.clicks)} />
                    <Metric theme={theme} label="CTR" value={`${(c.ctr || 0).toFixed(2)}%`} />
                    <Metric theme={theme} label="CPC" value={formatCurr(c.cpc)} />
                    <Metric theme={theme} label="Frecuencia" value={(c.frequency || 0).toFixed(2)} />
                    <Metric theme={theme} label="ROI" value={`${(c.roi || 0) >= 0 ? '+' : ''}${(c.roi || 0).toFixed(1)}%`}
                      colorClass={(c.roi || 0) > 0 ? 'text-success' : (c.roi || 0) < 0 ? 'text-error' : null} />
                  </div>
                  {c.last_activity && (
                    <div className={`mt-3 pt-3 border-t ${theme.border} flex items-center gap-2 text-xs ${theme.textSecondary}`}>
                      <Clock className="w-3 h-3" />
                      <span>Última actividad: {c.last_activity}</span>
                      <span className="opacity-50">·</span>
                      <span>{c.days_active} {c.days_active === 1 ? 'día' : 'días'} activos</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}


function CreativeCarousel({ campaignId, campaignName, fallbackGradient, fallbackInitials, platformLabel, platformColor, statusLabel, statusBadge, token }) {
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [imgError, setImgError] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchCreatives = async () => {
      try {
        const res = await axios.get(`${API}/campaigns/${campaignId}/creatives`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setCreatives(res.data || []);
      } catch (e) {
        if (!cancelled) setCreatives([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCreatives();
    return () => { cancelled = true; };
  }, [campaignId, token]);

  // Filtrar creativos que tienen imagen válida
  const valid = creatives.filter(cr => (cr.thumbnail_url || cr.image_url) && !imgError[cr.ad_id]);
  const current = valid[idx];

  const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + valid.length) % valid.length); };
  const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % valid.length); };

  // Sin creativos → placeholder con iniciales
  if (loading || valid.length === 0) {
    return (
      <div className={`relative h-40 bg-gradient-to-br ${fallbackGradient} flex items-center justify-center`}>
        {loading ? (
          <div className="text-white/70 text-xs font-mono">cargando...</div>
        ) : (
          <span className="text-4xl font-bold text-white opacity-90">{fallbackInitials}</span>
        )}
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${platformColor}`}>{platformLabel}</span>
        </div>
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium border bg-white/90 ${statusBadge}`}>{statusLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-40 bg-slate-900 overflow-hidden group">
      <img
        src={current.image_url || current.thumbnail_url}
        alt={current.ad_name || campaignName}
        className="w-full h-full object-cover"
        onError={() => setImgError(prev => ({ ...prev, [current.ad_id]: true }))}
      />
      {/* Indicador de video */}
      {current.video_id && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
            <Play className="w-6 h-6 text-white fill-white" />
          </div>
        </div>
      )}

      {/* Badges */}
      <div className="absolute top-3 left-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${platformColor}`}>{platformLabel}</span>
      </div>
      <div className="absolute top-3 right-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium border bg-white/90 ${statusBadge}`}>{statusLabel}</span>
      </div>

      {/* Navegación carousel */}
      {valid.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
          {/* Contador */}
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-xs font-mono">
            {idx + 1}/{valid.length}
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ theme, label, value, colorClass }) {
  return (
    <div className="flex flex-col">
      <span className={`text-xs ${theme.textSecondary}`}>{label}</span>
      <span className={`font-mono text-sm font-semibold ${colorClass || theme.text}`}>{value}</span>
    </div>
  );
}
